import { api, API_BASE, getCsrfToken } from './client';
import type { Category, PipelineStage, Contact, Interaction } from '../types/crm';

export function fetchCategories(): Promise<Category[]> {
  return api.get<Category[]>('/api/categories');
}

export function fetchPipelineStages(): Promise<PipelineStage[]> {
  return api.get<PipelineStage[]>('/api/pipeline-stages');
}

export function fetchContacts(archived = false): Promise<Contact[]> {
  return api.get<Contact[]>(`/api/contacts?archived=${archived}`);
}

export function fetchContact(id: string): Promise<Contact> {
  return api.get<Contact>(`/api/contacts/${id}`);
}

export function createContact(data: {
  first_name: string;
  last_name: string;
  company?: string;
  email?: string;
  phone?: string;
  notes?: string;
  category_id?: string;
  outreach_type: string;
  stage_id: string;
  next_follow_up_date?: string;
  lost_reason?: string;
  job_title?: string;
  gender?: string;
  employment_status?: string;
  owner?: string;
  linkedin?: string;
}): Promise<{ id: string }> {
  return api.post<{ id: string }>('/api/contacts', data);
}

export function exportContacts(): void {
  window.location.href = `${API_BASE}/api/contacts/export`;
}

export interface ImportResult {
  imported: number;
  updated: number;
  errors: { row: number; message: string }[];
}

export async function importContacts(file: File): Promise<ImportResult> {
  const text = await file.text();
  const csrf = await getCsrfToken();
  const res = await fetch(`${API_BASE}/api/contacts/import`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'text/csv', 'X-CSRF-Token': csrf },
    body: text,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? 'Import failed');
  return data as ImportResult;
}

export function updateContact(id: string, data: Partial<Contact>): Promise<{ ok: boolean }> {
  return api.patch<{ ok: boolean }>(`/api/contacts/${id}`, data);
}

export function batchUpdateStage(ids: string[], stageId: string): Promise<{ updated: number }> {
  return api.patch<{ updated: number }>('/api/contacts/batch', { ids, stage_id: stageId });
}

export function batchDeleteContacts(ids: string[]): Promise<{ deleted: number }> {
  return api.post<{ deleted: number }>('/api/contacts/batch-delete', { ids });
}

export function fetchInteractions(contactId: string): Promise<Interaction[]> {
  return api.get<Interaction[]>(`/api/interactions?contact_id=${contactId}`);
}

export function createInteraction(data: { contact_id: string; type: string; date?: string; notes?: string }): Promise<{ id: string; date: string; created_at: string }> {
  return api.post<{ id: string; date: string; created_at: string }>('/api/interactions', data);
}

export function updateInteraction(id: string, data: Partial<{ type: string; date: string; notes: string }>): Promise<{ ok: boolean }> {
  return api.patch<{ ok: boolean }>(`/api/interactions/${id}`, data);
}

export function deleteInteraction(id: string): Promise<void> {
  return api.delete(`/api/interactions/${id}`);
}
